import { toursApi } from "@api";
import { AddToCart, ClipLoaderCommon, StyledBookingTimeError } from "@components";
import { BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS } from "@constants";
import { CheckProductAvailsDateRange, BookingPriceGroup, ITourFareBookingTime } from "@interfaces";
import { breakpoint, theme } from "@utilities";
import _ from "lodash";
import { memo, useEffect, useState } from "react";
import { LoadingStage } from "src/Interfaces/shared";
import { formatBookingTime, shouldAllowAvailabilityCheck } from "src/Utilities/cartHelper";
import styled from "styled-components";

interface IPriceGroupProps {
    bookingSystem: string | null;
    productClass: string | null;
    priceGroup: BookingPriceGroup;
    range: CheckProductAvailsDateRange;
    checkAvailability: (bookingTimeID: string, bookingDetails: ITourFareBookingTime) => void;
    isPriceLoading?: boolean;
    priceOnly?: boolean;
}

export const ProductPriceGroup = memo((props: IPriceGroupProps) => {
    const { bookingSystem, priceGroup, range, checkAvailability, isPriceLoading, priceOnly } = props;
    const rawNumPax = priceGroup?.prices?.[0]?.numPax ?? 0;
    const rawPrice = priceGroup?.rrp ?? "0";
    const rawOriginalPrice = priceGroup?.rrpWas ?? null;
    // remove everything except digits, decimal point, and minus sign
    const numPax = parseFloat(String(rawNumPax).replace(/[^\d.-]/g, "")) || 0;
    const price = parseFloat(String(rawPrice).replace(/[^\d.-]/g, "")) || 0;
    const originalPrice = rawOriginalPrice
        ? parseFloat(String(rawOriginalPrice).replace(/[^\d.-]/g, ""))
        : null;

    const numPaxPrice = price * numPax;
    const numPaxOriginalPrice = originalPrice ? originalPrice * numPax : null;
    const savings =
        numPaxOriginalPrice && numPaxPrice > 0 && numPaxPrice < numPaxOriginalPrice
            ? numPaxOriginalPrice - numPaxPrice
            : 0;

    const [hasBookingTimeError, setHasBookingTimeError] = useState(
        priceGroup.hasBookingTimeError === true,
    );
    const needsBookingTimeId = !_.includes(
        BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS,
        bookingSystem?.trim().toLowerCase(),
    );
    const [shouldShowBookingTimeIds, setShouldShowBookingTimeIds] = useState(needsBookingTimeId);
    const [bookingTimeIdLoadingStage, setBookingTimeIdLoadingStage] = useState(
        LoadingStage.NOT_INITIALIZED,
    );
    const [bookingDetails, setBookingDetails] = useState<ITourFareBookingTime | null>(null);
    const [selectedBookingTimeId, setSelectedBookingTimeId] = useState<string>("0");

    useEffect(() => {
        const loadBookingTimeIds = async (productPricesDetailsId: string | number) => {
            try {
                setBookingTimeIdLoadingStage(LoadingStage.IS_INITIALIZING);
                const getBookingDetailResp =
                    await toursApi.getBookingDetail(productPricesDetailsId);
                const bookingDetails = getBookingDetailResp.data;
                setBookingDetails(bookingDetails);
                if (
                    bookingDetails.bookingTimes.length === 1 &&
                    bookingDetails.bookingTimes[0].BookingTime === "Daily availability"
                ) {
                    setShouldShowBookingTimeIds(false);
                    setSelectedBookingTimeId(
                        bookingDetails.bookingTimes[0].BookingTimeID.toString(),
                    );
                } else {
                    setShouldShowBookingTimeIds(true);
                    const defaultBookingTime =
                        bookingDetails.bookingTimes.find((bt) => bt.Default) ||
                        bookingDetails.bookingTimes[0];
                    setSelectedBookingTimeId(
                        defaultBookingTime?.BookingTimeID.toString() || selectedBookingTimeId,
                    );
                }
                setBookingTimeIdLoadingStage(LoadingStage.INITIALIZE_COMPLETE);
            } catch (error) {
                console.error(error);
                setHasBookingTimeError(true);
                setBookingTimeIdLoadingStage(LoadingStage.INITIALIZE_FAILURE);
            }
        };

        if (priceOnly) return;
        if (needsBookingTimeId) {
            loadBookingTimeIds(_.head(priceGroup.prices)!.productPricesDetailsId);
        }
    }, [bookingSystem]);

    return (
        <Content>
            <Fare>
                <div className="text">{priceGroup.name}</div>
                {priceGroup.travelTimeMessage && (
                    <div className="travel-time">{priceGroup.travelTimeMessage}</div>
                )}
            </Fare>
            {isPriceLoading ? (
                <LoadingPriceContainer>
                    <ClipLoaderCommon size={22} color={theme.colors.highlight} margin={0} />
                </LoadingPriceContainer>
            ) : (
                <PriceContainer>
                    <PriceRow>
                        <CurrentPrice>${numPaxPrice.toFixed(2)}</CurrentPrice>
                    </PriceRow>
                    {savings > 0 && (
                        <SavingsRow>
                            <WasPrice>was ${numPaxOriginalPrice?.toFixed(2)}</WasPrice>
                            <SaveBadge>SAVE ${savings.toFixed(2)}</SaveBadge>
                        </SavingsRow>
                    )}
                    {hasBookingTimeError && priceGroup.levyMessage && (
                        <div className="note">{priceGroup.levyMessage}</div>
                    )}
                </PriceContainer>
            )}

            {!priceOnly && (!priceGroup.hasBookingTimeError ? (
                <div className="listBtn">
                    {shouldShowBookingTimeIds &&
                        bookingTimeIdLoadingStage === LoadingStage.INITIALIZE_COMPLETE && (
                            <div className="time">
                                {bookingDetails?.bookingTimes.map((bookingTime) => {
                                    const isChecked =
                                        selectedBookingTimeId ===
                                        bookingTime.BookingTimeID.toString();
                                    return (
                                        <span key={`${priceGroup.id}-${bookingTime.BookingTimeID}`}>
                                            <input
                                                type="radio"
                                                id={`${priceGroup.id}-time-${bookingTime.BookingTimeID}`}
                                                checked={isChecked}
                                                value={bookingTime.BookingTimeID.toString()}
                                                onChange={() =>
                                                    setSelectedBookingTimeId(
                                                        bookingTime.BookingTimeID.toString(),
                                                    )
                                                }
                                            />
                                            <StyledLabel
                                                htmlFor={`${priceGroup.id}-time-${bookingTime.BookingTimeID}`}
                                            >
                                                <StyledRadioButton isChecked={isChecked}>
                                                    {isChecked && <StyledRadioInner />}
                                                </StyledRadioButton>
                                                <span>
                                                    {formatBookingTime(bookingTime.BookingTime)}
                                                </span>
                                            </StyledLabel>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    <StyledAddToCart
                        color={theme.gradients.button}
                        onClick={() => checkAvailability(selectedBookingTimeId, bookingDetails!)}
                        disabled={
                            !shouldAllowAvailabilityCheck(range, priceGroup) ||
                            bookingTimeIdLoadingStage === LoadingStage.IS_INITIALIZING
                        }
                        data-gtm-id="check-availability"
                    >
                        {bookingTimeIdLoadingStage === LoadingStage.IS_INITIALIZING ? (
                            <ClipLoaderCommon size={24} color={theme.colors.highlight} margin={0} />
                        ) : (
                            "Check Availability"
                        )}
                    </StyledAddToCart>
                </div>
            ) : (
                <div className="listBtn">
                    <StyledBookingTimeError>
                        We are unable to check live availability for this fare right now. Please try
                        again later.
                    </StyledBookingTimeError>
                </div>
            ))}
        </Content>
    );
});

const LoadingPriceContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    align-self: center;

    ${breakpoint.breakIpad`
        min-width: 120px;
    `}
`;

const PriceContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-shrink: 0;
    font-size: 24px;
    align-self: center;

    ${breakpoint.breakIpad`
        min-width: 120px;
    `}

    ${breakpoint.breakMobile`
        align-self: flex-start;
    `}

    .note {
        font-size: 13px;
        color: #828282;
        margin-top: 4px;
        width: 150px;
    }
`;

const PriceRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const CurrentPrice = styled.span`
    font-size: ${theme.colors.price ? '20px' : '24px'} !important;
    font-weight: 700 !important;
    color: ${theme.colors.price || theme.colors.highlight} !important;
    transform: none !important;

    ${breakpoint.breakMobile`
        font-size: ${theme.colors.price ? '18px' : '20px'} !important;
    `}
    ${breakpoint.breakIpad`
        font-size: ${theme.colors.price ? '20px' : '24px'} !important;
    `}
`;

const SavingsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const WasPrice = styled.span`
    position: relative;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: #6b7280 !important;
    transform: none !important;
    ${theme.colors.price
        ? `
        text-decoration: none !important;
        &::after {
            content: '';
            position: absolute;
            left: -2px;
            right: -2px;
            top: 0;
            bottom: 0;
            background: linear-gradient(
                to top left,
                transparent calc(50% - 1px),
                ${theme.colors.price} calc(50% - 1px),
                ${theme.colors.price} calc(50% + 1px),
                transparent calc(50% + 1px)
            );
        }
    `
        : `text-decoration: line-through !important;`}
`;

const SaveBadge = styled.span`
    background-color: ${theme.colors.main} !important;
    color: #000 !important;
    font-size: 12px !important;
    font-weight: 500 !important;
    padding: 4px 12px;
    border-radius: 999px;
    transform: none !important;
`;

const Content = styled.div`
    background-color: #fff;
    width: 100%;
    padding: 14px 20px;
    border-radius: 8px;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    ${breakpoint.breakMobile` 
        padding: 12px 16px;
        gap: 10px;
        flex-direction: column;
        align-items: flex-start;
        border-bottom: none;
        border-radius: 0;
    `}
    .listBtn {
        flex-shrink: 0;
        width: 320px;
        align-self: center;

        .time {
            display: grid;
            justify-content: space-evenly;
            column-gap: 8px;
            grid-template-columns: 1fr 1fr 1fr;
            font-size: 14px;
            span {
                position: relative;
                display: flex;
                align-items: center;
                gap: 6px;
                margin-bottom: 5px;
                white-space: nowrap;
            }

            input {
                display: none;
            }
        }

        ${breakpoint.breakIpad`
            width: 240px;
            
            .time {
                grid-template-columns: 1fr 1fr;
            }
        `}
        ${breakpoint.breakMobile`   
            width: 100%;
            span {
            margin-left: 2px;
        }
        `}
    }
`;

const StyledRadioButton = styled.span<{ isChecked: boolean }>`
    width: 24px;
    height: 24px;
    padding: 4px;
    border-radius: 50%;
    border: ${(props) =>
        props.isChecked ? `2px solid ${props.theme.colors.time_radio}` : "2px solid #daddde"};
    background-color: ${(props) =>
        props.isChecked ? props.theme.colors.time_radio : "transparent"};
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
    cursor: pointer;
`;

const StyledRadioInner = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #fff;
`;

const Fare = styled.div`
    background-color: transparent;
    flex-shrink: 0;
    align-self: center;

    ${breakpoint.breakIpad`
        max-width: 200px;
    `}

    ${breakpoint.breakMobile`
        align-self: flex-start;
    `}

    .text {
        font-weight: 600;
        line-height: 1.3;
        font-size: 15px;
        width: 300px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: #090a18;

        ${breakpoint.breakIpad` 
            font-size: 13px;
            width: 200px;
        `}
        ${breakpoint.breakIpadPro`
            font-size: 15px;
            width: 300px;
        `}
        ${breakpoint.breakOnlyMobile`
            width: 100%;
            white-space: normal;
        `}
    }
    .travel-time {
        font-weight: normal;
        line-height: 1.3;
        font-size: 13px;
        color: ${(props) => props.theme.colors.gray_3};
        margin-top: 2px;
        ${breakpoint.breakIpad` 
            font-size: 11px;
        `}
        ${breakpoint.breakIpadPro`
            font-size: 13px;
        `}
    }
`;

const StyledLabel = styled.label`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

const StyledAddToCart = styled(AddToCart)`
    width: 100%;

    ${breakpoint.breakMobile`
        background: ${(props) => props.theme.colors.main} !important;
        border-radius: 8px;
        font-weight: 700;
        font-size: 16px;
        padding: 16px;
        margin-top: 16px;
    `}
`;
